import { differenceInDays } from "date-fns";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { UsersAPI } from "generated/types";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";
import { useProduct } from "hooks/useProduct";
import { useReactTooltip } from "hooks/useReactTooltip";
import { useLanguage } from "hooks/useSelectedLanguage";

import { SortableTable } from "components/SortableTable/SortableTable";
import type { TableRow } from "components/SortableTable/SortableTable.model";

import { CssButton, CssClickableIcon } from "styles/helpers/layout";

interface IndividualEmailInviteListProps {
  emailInviteBundleId: string;
  inviteList: UsersAPI.EmailInviteStatus[];
  showGroupColumn?: boolean;
}

export const IndividualEmailInviteList: React.FC<IndividualEmailInviteListProps> = ({
  emailInviteBundleId,
  inviteList = [],
  showGroupColumn = true,
}) => {
  useReactTooltip();
  const { t } = useTranslation(["common", "teacher-dashboard"]);
  const { currentLanguage } = useLanguage();
  const { formatDate } = useLocaleDateFormatter();

  const { loading: isLoading, deleteInviteById, fetchInvitationBundleList } = useProduct();
  const { fetchEmailInviteBundleDetails } = useInviteManagement();

  const onDeleteButtonClick = (inviteId: string) => {
    deleteInviteById(inviteId).then(() => {
      fetchInvitationBundleList();
      fetchEmailInviteBundleDetails(emailInviteBundleId);
    });
  };

  // Build object with information to display on rows
  const tableRowData = useMemo<TableRow[]>(
    () =>
      inviteList.map((invite) => {
        // Delete feature is only available for pending invites created in the past 14 days
        const isRemovableInvite =
          !invite.accepted && differenceInDays(new Date(), new Date(invite.createdOn)) <= 14;

        return {
          id: invite.id,
          dataCy: "individual-invite-row",
          cells: [
            {
              id: `invite-${invite.id}-email`,
              dataCy: "individual-invite-email",
              content: invite.email,
            },
            {
              id: `invite-${invite.id}-accepted-date`,
              dataCy: "individual-invite-accepted-date",
              content: invite.acceptedOn ? formatDate(invite.acceptedOn, "dd-MM-yyyy") : "-",
            },
            ...(showGroupColumn
              ? [
                  {
                    id: `invite-${invite.id}-grouped`,
                    dataCy: "individual-invite-grouped",
                    content: invite.inTargetGroup
                      ? t(
                          "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columnValues.inTargetGroup.yes",
                        )
                      : t(
                          "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columnValues.inTargetGroup.no",
                        ),
                  },
                ]
              : []),
            {
              id: `invite-${invite.id}-actions`,
              dataCy: "individual-invite-actions",
              // Delete feature is only available for pending invites created in the past 14 days
              content: (
                <CssButton
                  aria-label={t("common:iconButtonLabel.delete")}
                  data-cy="delete-individual-email-invite-button"
                  data-tip={
                    isRemovableInvite
                      ? t("teacher-dashboard:studentLicense.invitationList.tooltip.removableInvite")
                      : t("teacher-dashboard:studentLicense.invitationList.tooltip.removalExpired")
                  }
                  variant="text"
                  disabled={isLoading || !isRemovableInvite}
                  onClick={() => onDeleteButtonClick(invite.id)}
                >
                  <CssClickableIcon iconName="far fa-trash-alt" />
                </CssButton>
              ),
            },
          ],
        };
      }),
    [inviteList, currentLanguage],
  );

  return (
    <SortableTable
      variant="white"
      headerCells={[
        {
          id: "individual-invite-email-column",
          title: t(
            "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.email",
          ),
          size: "3fr",
        },
        {
          id: "individual-invite-accepted-on-column",
          title: t(
            "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.acceptedDate",
          ),
          size: "2fr",
        },
        // Only show group column for invites that have a group assigned, otherwise it adds no value
        ...(showGroupColumn
          ? [
              {
                id: "individual-invite-grouped-column",
                title: t(
                  "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.inTargetGroup",
                ),
                size: "1fr",
              },
            ]
          : []),
        {
          id: "individual-invite-actions-column",
          title: t(
            "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.actions",
          ),
          size: "1fr",
        },
      ]}
      rows={tableRowData}
      noResultsMessage={t(
        "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.noResultsMessage",
      )}
    />
  );
};
